import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarClock, Pencil, Trash2, UserRound } from 'lucide-react';
import { Button, Card, Dialog, DialogBody, DialogFooter, DialogHeader, Menu, Pill } from '../../../components/ui';
import { deleteIssue, issuesErrorMessage, updateIssue } from '../issuesApi';
import type { BalanceOfForces, IssueView } from '../../../types/issues';
import {
  STATUS_LABEL,
  balanceDescription,
  balanceShares,
  decisionIsNear,
  decisionPhrase,
  formatDate,
  formatIndex,
  indexOffset,
  positionCounts,
  positionLabel,
  positionStyle,
  priorityLabel,
  statusToneOf,
} from './format';
import { IssueFormDialog } from './IssueFormDialog';
import { PositionGlyph, PositionPill } from './PositionMark';

/* ------------------------------------------------------------------ */
/* The bar itself                                                      */
/* ------------------------------------------------------------------ */

/**
 * Influence-weighted opposition, neutrality and support as one strip, with the
 * net index marked on it. Opposition is hatched and support solid, so the two
 * camps stay apart when the colour is gone.
 */
export const BalanceStrip: React.FC<{
  balance: BalanceOfForces | null | undefined;
  /** Taller bar with the index marker, for the issue header */
  size?: 'sm' | 'md';
  className?: string;
}> = ({ balance, size = 'sm', className = '' }) => {
  const shares = balanceShares(balance);
  const height = size === 'md' ? 'h-2.5' : 'h-1.5';

  if (shares.empty) {
    return (
      <div className={className} role="img" aria-label="No positions recorded yet">
        <div className={`w-full rounded-full bg-neutral-100 ${height}`} />
      </div>
    );
  }

  return (
    <div className={className} role="img" aria-label={balanceDescription(balance)}>
      <div className={`relative flex w-full overflow-hidden rounded-full bg-neutral-100 ${height}`}>
        {shares.oppose > 0 && (
          <span
            className="block h-full"
            title={`Opposition ${Math.round(shares.oppose)}%`}
            style={{
              width: `${shares.oppose}%`,
              backgroundColor: '#ea2a33',
              backgroundImage:
                'repeating-linear-gradient(135deg, rgba(255,255,255,0.45) 0 2px, rgba(255,255,255,0) 2px 5px)',
            }}
          />
        )}
        {shares.neutral > 0 && (
          <span
            className="block h-full bg-neutral-300"
            title={`Neutral ${Math.round(shares.neutral)}%`}
            style={{ width: `${shares.neutral}%` }}
          />
        )}
        {shares.support > 0 && (
          <span
            className="block h-full bg-emerald-500"
            title={`Support ${Math.round(shares.support)}%`}
            style={{ width: `${shares.support}%` }}
          />
        )}
      </div>
      {size === 'md' && (
        <div className="relative mt-1 h-3">
          <span className="absolute inset-x-0 top-[5px] h-px bg-neutral-200" aria-hidden="true" />
          <span className="absolute left-1/2 top-0 h-3 w-px -translate-x-1/2 bg-neutral-200" aria-hidden="true" />
          <span
            className="absolute top-[1px] h-2.5 w-2.5 -translate-x-1/2 rounded-full border-2 border-white bg-ink"
            style={{ left: `${indexOffset(balance?.index ?? 0)}%` }}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
};

/** The five counts in board order, shapes first so they read without colour */
export const PositionCounts: React.FC<{
  balance: BalanceOfForces | null | undefined;
  /** Tighter and on one line, for list rows */
  compact?: boolean;
  className?: string;
}> = ({ balance, compact = false, className = '' }) => (
  <div
    className={`flex items-center ${compact ? 'gap-x-1.5' : 'flex-wrap gap-x-3 gap-y-1.5'} ${className}`}
  >
    {positionCounts(balance).map(({ position, count }) => (
      <span
        key={position}
        className={`inline-flex items-center ${compact ? 'gap-1' : 'gap-1.5'} text-[12px] tabular-nums ${
          count ? 'text-neutral-600' : 'text-neutral-300'
        }`}
        title={positionLabel(position)}
      >
        <PositionGlyph position={position} className={count ? '' : 'opacity-40'} />
        {count}
        <span className="sr-only">{positionLabel(position)}</span>
      </span>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Issue header strip                                                  */
/* ------------------------------------------------------------------ */

interface BalanceBarProps {
  issue: IssueView;
  /** Called after the issue is edited, so the workspace reloads it */
  onChange: () => void;
}

/**
 * Where the issue stands, above every view of it: the balance of forces, what
 * we want, and the facts that decide how urgent it is.
 */
export const BalanceBar: React.FC<BalanceBarProps> = ({ issue, onChange }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const balance = issue.balance;
  const index = balance?.index ?? 0;
  const unknown = balance?.unknownInfluence ?? 0;
  const ourStyle = positionStyle(issue.ourPosition);
  const near = decisionIsNear(issue.decisionDate);

  const setStatus = async (status: IssueView['status']) => {
    setBusy(true);
    setError(null);
    try {
      await updateIssue(issue.id, { status });
      onChange();
    } catch (err) {
      setError(issuesErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteIssue(issue.id);
      navigate('/issues');
    } catch (err) {
      setError(issuesErrorMessage(err));
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Card padded className="mt-1">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">
            <Pill tone={statusToneOf(issue.status)}>{STATUS_LABEL[issue.status]}</Pill>
            <span className="text-[12px] text-neutral-400">{priorityLabel(issue.priority)}</span>
            {issue.categoryLabel && <span className="text-[12px] text-neutral-400">· {issue.categoryLabel}</span>}
            {issue.owner && (
              <span className="inline-flex items-center gap-1 text-[12px] text-neutral-400">
                <UserRound className="h-3.5 w-3.5" strokeWidth={1.75} />
                {issue.owner.name}
              </span>
            )}
            {issue.decisionDate && (
              <span
                className={`inline-flex items-center gap-1 text-[12px] ${near ? 'text-primary-700' : 'text-neutral-400'}`}
                title={`Decision ${formatDate(issue.decisionDate)}`}
              >
                <CalendarClock className="h-3.5 w-3.5" strokeWidth={1.75} />
                Decision {decisionPhrase(issue.decisionDate)}
              </span>
            )}
          </div>
          <Menu
            align="right"
            items={[
              { label: 'Edit issue', icon: Pencil, onSelect: () => setEditing(true) },
              ...(issue.status !== 'active'
                ? [{ label: 'Mark active', onSelect: () => setStatus('active'), disabled: busy }]
                : []),
              ...(issue.status !== 'decided'
                ? [{ label: 'Mark decided', onSelect: () => setStatus('decided'), disabled: busy }]
                : []),
              ...(issue.status !== 'dormant'
                ? [{ label: 'Mark dormant', onSelect: () => setStatus('dormant'), disabled: busy }]
                : []),
              { label: 'Delete issue', icon: Trash2, danger: true, onSelect: () => setConfirmDelete(true) },
            ]}
            trigger={(props) => (
              <Button size="sm" variant="secondary" {...props}>
                Issue
                <span aria-hidden="true">···</span>
              </Button>
            )}
          />
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex flex-shrink-0 items-center gap-3">
            <div>
              <p className="text-[12px] text-neutral-500">Balance</p>
              <p className="mt-0.5 text-[22px] leading-none tracking-[-0.02em] tabular-nums text-ink">
                {formatIndex(index)}
              </p>
            </div>
            <div className="h-8 w-px bg-neutral-200/70" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-[12px] text-neutral-500">We want</p>
              <PositionPill position={issue.ourPosition} className="mt-1" />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <BalanceStrip balance={balance} size="md" />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
              <PositionCounts balance={balance} />
              <span className="text-[12px] text-neutral-400">
                {issue.stanceCount} position{issue.stanceCount === 1 ? '' : 's'}
                {unknown > 0 && ` · ${Math.round(unknown)} influence unplaced`}
              </span>
            </div>
          </div>
        </div>

        {issue.ourPositionNote && (
          <p className="mt-3 border-t border-neutral-200/70 pt-3 text-[12.5px] text-neutral-500">
            <span className={ourStyle.text}>Our line</span> · {issue.ourPositionNote}
          </p>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}
      </Card>

      {editing && (
        <IssueFormDialog
          issue={issue}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChange();
          }}
        />
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} label="Delete issue" handle className="sm:max-w-md">
        <DialogHeader title="Delete this issue?" onClose={() => setConfirmDelete(false)} />
        <DialogBody>
          <p className="text-[13px] text-neutral-600">
            “{issue.title}” and every position recorded on it will be removed. This cannot be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={busy} onClick={remove}>
            Delete
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

/** A quiet link to the workspace used by profile sections and the list */
export const IssueLink: React.FC<{ issue: { id: string; slug?: string | null; title: string }; className?: string }> = ({
  issue,
  className = '',
}) => (
  <Link
    to={`/issues/${issue.slug || issue.id}`}
    className={`truncate text-[13px] text-ink transition-colors hover:text-primary-700 ${className}`}
  >
    {issue.title}
  </Link>
);

export default BalanceBar;
